import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { searchHubOptions } from '@/services/api/pitstops';
import { onSessionClear } from '@/utils/sessionReset';
import { storage } from '@/utils/storage';

import { useLoad } from './hooks/useLoad';

/**
 * Feature-wide state for the WMS screens: the hub the operator is working in
 * (persisted per browser), the hub list for every picker and a refresh token
 * the pages key their fetches on.
 */

export interface WarehouseHubOption {
  id: number;
  name: string;
  code: string | null;
  /**
   * Contact + place from the hub master (pitstop): a document shipping to this
   * hub prefills its destination from them. Missing = unknown.
   */
  address?: string | null;
  phoneNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface WarehouseContextValue {
  hubs: WarehouseHubOption[];
  hubsLoading: boolean;
  /** Selected hub; null = "Semua hub". */
  hubID: number | null;
  hub: WarehouseHubOption | null;
  /** False until the initial hub choice is resolved (avoid double fetches). */
  ready: boolean;
  setHubID: (id: number | null) => void;
  /** Bumped by `refresh()`; list/detail fetches depend on it. */
  refreshToken: number;
  /** Refetch the current screen (header Refresh). */
  refresh: () => void;
}

const HUB_KEY = 'wms_hub_id';

const noop = () => undefined;

export const WarehouseContext = createContext<WarehouseContextValue>({
  hubs: [],
  hubsLoading: false,
  hubID: null,
  hub: null,
  ready: true,
  setHubID: noop,
  refreshToken: 0,
  refresh: noop,
});

export const useWarehouse = (): WarehouseContextValue =>
  useContext(WarehouseContext);

const readStoredHub = (): number | null | undefined => {
  const raw = storage.getItem(HUB_KEY);
  if (raw === null) return undefined;
  if (raw === 'all') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/* ------------------------- hub list (per session) ------------------------ */

/** Hubs barely change; fetch once per session, refresh after this long. */
export const HUBS_TTL_MS = 10 * 60 * 1000;

let hubCache: { at: number; list: WarehouseHubOption[] } | null = null;
let hubRequest: Promise<WarehouseHubOption[]> | null = null;

const freshHubs = (): WarehouseHubOption[] | null =>
  hubCache && Date.now() - hubCache.at < HUBS_TTL_MS ? hubCache.list : null;

/** The hub list, from the session cache when fresh (one request in flight). */
export const loadWarehouseHubs = (): Promise<WarehouseHubOption[]> => {
  const fresh = freshHubs();
  if (fresh) return Promise.resolve(fresh);
  if (!hubRequest) {
    const request = searchHubOptions()
      .then((opts) => {
        const list = opts
          .map((o) => ({
            id: Number(o.id),
            name: o.name,
            code: o.code || null,
            address: o.address ?? null,
            phoneNumber: o.phoneNumber ?? null,
            latitude: o.latitude ?? null,
            longitude: o.longitude ?? null,
          }))
          .filter((h) => Number.isFinite(h.id));
        hubCache = { at: Date.now(), list };
        return list;
      })
      .finally(() => {
        if (hubRequest === request) hubRequest = null;
      });
    hubRequest = request;
  }
  return hubRequest;
};

/** Forget the cached hub list (sign-out, tests). */
export const resetWarehouseHubCache = (): void => {
  hubCache = null;
  hubRequest = null;
};
onSessionClear(resetWarehouseHubCache);

/* -------------------------------- provider ------------------------------- */

const NO_HUBS: WarehouseHubOption[] = [];

export const WarehouseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const stored = useMemo(readStoredHub, []);
  const cached = useMemo(freshHubs, []);
  const hubsLoad = useLoad(() => loadWarehouseHubs(), [], !cached);
  const hubs = cached ?? hubsLoad.data ?? NO_HUBS;
  const hubsSettled = !!cached || !!hubsLoad.data || !!hubsLoad.error;
  const hubsLoading = !cached && hubsLoad.loading;

  /** The operator's explicit choice; undefined = default to the first hub. */
  const [chosen, setChosen] = useState<number | null | undefined>(stored);
  const ready = chosen !== undefined || hubsSettled;
  const hubID =
    chosen !== undefined ? chosen : ready ? (hubs[0]?.id ?? null) : null;

  const [refreshToken, setRefreshToken] = useState(0);

  const setHubID = useCallback((id: number | null) => {
    storage.setItem(HUB_KEY, id === null ? 'all' : String(id));
    setChosen(id);
  }, []);

  const refresh = useCallback(() => setRefreshToken((n) => n + 1), []);

  const hub = useMemo(() => {
    if (hubID === null) return null;
    return (
      hubs.find((h) => h.id === hubID) ?? {
        id: hubID,
        name: `Hub #${hubID}`,
        code: null,
      }
    );
  }, [hubs, hubID]);

  const value = useMemo<WarehouseContextValue>(
    () => ({
      hubs,
      hubsLoading,
      hubID,
      hub,
      ready,
      setHubID,
      refreshToken,
      refresh,
    }),
    [hubs, hubsLoading, hubID, hub, ready, setHubID, refreshToken, refresh]
  );

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};
